import { PersonIcon } from '@/assets/icons'
import PageLoader from '@components/PageLoader'
import { useMe, useUpdateMe } from '@hooks'
import { useAuth } from '@context/AuthContext'
import React, { useState } from 'react'
import toast from 'react-hot-toast'
import {
	ProfileContainer,
	ProfileWrapper,
	ProfileHeader,
	ProfileTitle,
	ProfileContent,
	ProfileAvatar,
	ProfileInfo,
	ProfileName,
	ProfileEmail,
	ProfileActions,
	EditButton,
	ErrorState,
} from './ProfilePage.styles'
import EditProfilePictureModal from './EditProfilePictureModal'
import EditNameModal from './EditNameModal'

const ProfilePage: React.FC = () => {
	const { setAuth, token, user: authUser } = useAuth()
	const { data: user, isLoading, error } = useMe()
	const updateMe = useUpdateMe()
	const [isEditPictureModalOpen, setIsEditPictureModalOpen] = useState(false)
	const [isEditNameModalOpen, setIsEditNameModalOpen] = useState(false)

	if (isLoading) {
		return (
			<ProfileWrapper>
				<PageLoader label="Loading profile…" />
			</ProfileWrapper>
		)
	}

	if (error || !user) {
		return (
			<ProfileWrapper>
				<ProfileContainer>
					<ErrorState>Error loading profile. Please try again.</ErrorState>
				</ProfileContainer>
			</ProfileWrapper>
		)
	}

	const handleUpdateName = (firstName: string, lastName: string) => {
		updateMe.mutate(
			{ firstName, lastName },
			{
				onSuccess: (updatedUser) => {
					toast.success('Name updated successfully!')
					setIsEditNameModalOpen(false)
					// Update auth context with new user data
					if (authUser) {
						setAuth({
							token,
							user: {
								...authUser,
								firstName: updatedUser.firstName || undefined,
								lastName: updatedUser.lastName || undefined,
							},
						})
					}
				},
				onError: () => {
					toast.error('Failed to update name. Please try again.')
				},
			}
		)
	}

	return (
		<ProfileWrapper>
			<ProfileContainer>
				<ProfileHeader>
					<ProfileTitle>My Profile</ProfileTitle>
				</ProfileHeader>

				<ProfileContent>
					<ProfileAvatar>
						{user.profilePicture ? (
							<img src={user.profilePicture} alt={`${user.firstName || ''} ${user.lastName || ''}`} />
						) : (
							<PersonIcon width={120} height={120} fill="currentColor" />
						)}
					</ProfileAvatar>

					<ProfileInfo>
						<ProfileName>
							{user.firstName || ''} {user.lastName || ''}
						</ProfileName>
						<ProfileEmail>{user.email}</ProfileEmail>
					</ProfileInfo>

					<ProfileActions>
						<EditButton onClick={() => setIsEditPictureModalOpen(true)}>
							Edit Profile Picture
						</EditButton>
						<EditButton onClick={() => setIsEditNameModalOpen(true)}>Edit Name</EditButton>
					</ProfileActions>
				</ProfileContent>
			</ProfileContainer>

			<EditProfilePictureModal
				isOpen={isEditPictureModalOpen}
				onClose={() => setIsEditPictureModalOpen(false)}
			/>

			<EditNameModal
				isOpen={isEditNameModalOpen}
				onClose={() => setIsEditNameModalOpen(false)}
				onSave={handleUpdateName}
				initialFirstName={user.firstName || ''}
				initialLastName={user.lastName || ''}
				isUpdating={updateMe.isPending}
			/>
		</ProfileWrapper>
	)
}

export default ProfilePage

